import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Activity, TrendingUp, TrendingDown, Minus, RefreshCw, ArrowRight,
  Target, CalendarRange, Loader2, PartyPopper,
  Users, Store, MapPin, ChevronDown, ChevronLeft, ChevronRight,
} from "lucide-react";
import confetti from "canvas-confetti";
import api from "@/lib/api";

// Palette (alignée sur le thème Shariti) — couleurs riches, jamais brutes.
const C = {
  orange: "#EE722E",
  yellow: "#E9B800",
  green: "#1F9D55",
  red: "#D64545",
  track: "rgba(24,24,24,0.08)",
};

const PERIODS = [
  { key: "today", label: "Aujourd'hui" },
  { key: "week", label: "Semaine" },
  { key: "month", label: "Mois" },
  { key: "year", label: "Année" },
  { key: "custom", label: "Personnalisé" },
];

// Compteur animé qui RE-anime à chaque changement de valeur (effet « temps réel »).
function useLiveCountUp(value, duration = 750) {
  const [display, setDisplay] = useState(Number(value) || 0);
  const fromRef = useRef(Number(value) || 0);
  const rafRef = useRef(null);
  useEffect(() => {
    const from = fromRef.current;
    const to = Number(value) || 0;
    if (from === to) { setDisplay(to); return; }
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(from + (to - from) * eased);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else fromRef.current = to;
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, duration]);
  return Math.round(display);
}

function frDate(iso) {
  if (!iso) return "";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}

// Décale une date ISO (AAAA-MM-JJ) de ``delta`` jours.
function shiftIso(iso, delta) {
  try {
    const d = new Date(iso + "T00:00:00");
    d.setDate(d.getDate() + delta);
    return d.toISOString().slice(0, 10);
  } catch { return iso; }
}

// Libellé long avec le jour de la semaine (ex. « samedi 30 août »).
function frDayLabel(iso) {
  if (!iso) return "";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }); }
  catch { return iso; }
}


// Petite salve de confettis aux couleurs Shariti (respecte « prefers-reduced-motion »).
function fireConfetti() {
  const colors = [C.orange, C.yellow, C.green];
  const base = { colors, disableForReducedMotion: true, scalar: 0.9, zIndex: 9999 };
  confetti({ ...base, particleCount: 90, spread: 72, startVelocity: 42, origin: { y: 0.4 } });
  setTimeout(() => confetti({ ...base, particleCount: 45, angle: 60, spread: 55, origin: { x: 0, y: 0.6 } }), 140);
  setTimeout(() => confetti({ ...base, particleCount: 45, angle: 120, spread: 55, origin: { x: 1, y: 0.6 } }), 280);
}

function ObjectiveReachedBadge() {
  return (
    <motion.span
      initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 400, damping: 16 }}
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold"
      style={{ color: "#157A43", background: "rgba(31,157,85,0.14)" }}
      data-testid="bulletins-objective-reached">
      <PartyPopper className="h-3.5 w-3.5" />
      Objectif atteint
    </motion.span>
  );
}

function DeltaBadge({ delta, prevLabel }) {
  if (delta == null) {
    return <span className="text-xs font-medium text-muted-ink">Pas de repère précédent</span>;
  }
  const up = delta > 0, flat = delta === 0;
  const Icon = up ? TrendingUp : flat ? Minus : TrendingDown;
  const color = up ? C.green : flat ? "rgba(24,24,24,0.55)" : C.red;
  const bg = up ? "rgba(31,157,85,0.12)" : flat ? "rgba(24,24,24,0.05)" : "rgba(214,69,69,0.12)";
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold"
      style={{ color, background: bg }} data-testid="bulletins-delta">
      <Icon className="h-3.5 w-3.5" />
      {up ? "+" : ""}{delta}%
      <span className="font-medium opacity-80">vs {prevLabel}</span>
    </span>
  );
}

function RadialGauge({ progress = 0, objective = 0, auto = false }) {
  const size = 160, stroke = 13, r = (size - stroke) / 2, circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, progress));
  const reached = pct >= 100;
  const color = reached ? C.green : pct >= 60 ? C.orange : C.yellow;
  const animPct = useLiveCountUp(pct, 800);
  return (
    <div className="flex flex-col items-center" data-testid="bulletins-gauge">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.track} strokeWidth={stroke} />
          <motion.circle
            cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} strokeLinecap="round"
            style={{ stroke: color }} strokeDasharray={circ}
            initial={false}
            animate={{ strokeDashoffset: circ - (circ * pct) / 100 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-extrabold tracking-tight text-ink">{animPct}%</span>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-ink">de l'objectif</span>
        </div>
      </div>
      <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[var(--border-soft)] bg-[var(--surface-2)] px-2.5 py-1 text-xs font-bold text-ink">
        <Target className="h-3.5 w-3.5" style={{ color }} />
        Objectif {Number(objective || 0).toLocaleString("fr-FR")}
        {auto && <span className="font-medium text-muted-ink">· auto</span>}
      </div>
    </div>
  );
}

function Sparkline({ points = [], caption }) {
  const counts = points.map((p) => p.count || 0);
  const path = useMemo(() => {
    if (counts.length < 2) return null;
    const w = 100, h = 40, pad = 3;
    const max = Math.max(...counts, 1);
    const stepX = w / (counts.length - 1);
    const coords = counts.map((c, i) => [i * stepX, h - pad - (c / max) * (h - pad * 2)]);
    const line = coords.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(2)} ${p[1].toFixed(2)}`).join(" ");
    const area = `${line} L ${w} ${h} L 0 ${h} Z`;
    return { line, area, last: coords[coords.length - 1], w, h };
  }, [counts]);

  if (!path) {
    return <div className="flex h-16 items-center justify-center text-xs text-muted-ink">Historique insuffisant</div>;
  }
  return (
    <div data-testid="bulletins-sparkline">
      <svg viewBox={`0 0 ${path.w} ${path.h}`} preserveAspectRatio="none" className="h-16 w-full">
        <defs>
          <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.orange} stopOpacity="0.28" />
            <stop offset="100%" stopColor={C.orange} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={path.area} fill="url(#spark-fill)" />
        <motion.path
          d={path.line} fill="none" stroke={C.orange} strokeWidth={2}
          vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
          transition={{ duration: 1.1, ease: "easeOut" }}
        />
        <circle cx={path.last[0]} cy={path.last[1]} r={2.6} fill={C.orange} vectorEffect="non-scaling-stroke" />
      </svg>
      {caption && <div className="mt-1 text-center text-[10px] font-medium text-muted-ink">{caption}</div>}
    </div>
  );
}

// ————— Répartition « qui a fait combien & où » (panneau repliable) —————————————
const RANK_MEDAL = ["#E9B800", "#9AA3AF", "#C2853F"]; // or / argent / bronze (top 3)

function RankBadge({ rank }) {
  const medal = rank <= 3 ? RANK_MEDAL[rank - 1] : null;
  if (medal) {
    return (
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-extrabold text-white"
        style={{ background: medal }} aria-label={`Rang ${rank}`}>
        {rank}
      </span>
    );
  }
  return (
    <span className="w-5 shrink-0 text-center text-[11px] font-bold text-muted-ink tabular-nums" aria-label={`Rang ${rank}`}>
      {rank}
    </span>
  );
}

function Avatar({ color, initials, icon: Icon }) {
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-extrabold text-white"
      style={{ background: color || C.orange }}>
      {Icon ? <Icon className="h-4 w-4" /> : initials}
    </span>
  );
}

function BreakdownRow({ rank, color, initials, icon, title, meta, metaIcon: MetaIcon, count, pct, assoc, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay }}
      className="flex items-center gap-3 px-5 py-2.5 transition-colors hover:bg-[var(--surface-2)]"
      data-testid={`bulletins-breakdown-row-${rank}`}
    >
      <RankBadge rank={rank} />
      <Avatar color={color} initials={initials} icon={icon} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1.5">
            {assoc?.acronym && (
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: assoc.color || C.orange }}
                title={assoc.acronym} />
            )}
            <span className="truncate text-[13px] font-bold text-ink">{title}</span>
          </span>
          <span className="shrink-0 text-[13px] font-extrabold tabular-nums text-ink">{count}</span>
        </div>
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full" style={{ background: C.track }}>
          <motion.div className="h-full rounded-full"
            style={{ background: rank <= 3 ? C.orange : "rgba(238,114,46,0.55)", transformOrigin: "left" }}
            initial={{ scaleX: 0 }} animate={{ scaleX: Math.max(0.04, pct) }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay }} />
        </div>
        {meta && (
          <div className="mt-1 flex items-center gap-1 truncate text-[11px] text-muted-ink">
            {MetaIcon && <MetaIcon className="h-3 w-3 shrink-0" />}
            <span className="truncate">{meta}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function BreakdownPanel({ open, range, refreshKey }) {
  const [mode, setMode] = useState("person"); // person | lieu
  const [bk, setBk] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const reqRef = useRef(0);

  useEffect(() => { if (!open) setExpanded(false); }, [open]);

  useEffect(() => {
    if (!open || !range?.start || !range?.end) return;
    const seq = ++reqRef.current;
    setLoading(true);
    api.get("/dashboard/bulletins-breakdown", { params: { from: range.start, to: range.end } })
      .then((r) => { if (seq === reqRef.current) setBk(r.data); })
      .catch(() => { if (seq === reqRef.current) setBk(null); })
      .finally(() => { if (seq === reqRef.current) setLoading(false); });
  }, [open, range?.start, range?.end, refreshKey]);

  const rows = mode === "person" ? (bk?.by_person || []) : (bk?.by_lieu || []);
  const max = rows.length ? rows[0].count : 1;
  const shown = expanded ? rows.slice(0, 100) : rows.slice(0, 6);
  const isEmpty = !loading && rows.length === 0;

  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden border-t border-[var(--border-soft)] bg-[var(--surface-1)]"
          data-testid="bulletins-breakdown-panel"
        >
          {/* Barre d'outils : bascule Par personne / Par lieu + résumé */}
          <div className="flex flex-wrap items-center justify-between gap-2 px-5 pb-2 pt-3">
            <div className="inline-flex rounded-full border border-[var(--border-soft)] bg-white p-0.5">
              {[
                { k: "person", label: "Par personne", Icon: Users },
                { k: "lieu", label: "Par lieu", Icon: Store },
              ].map(({ k, label, Icon }) => {
                const on = mode === k;
                return (
                  <button key={k} onClick={() => { setMode(k); setExpanded(false); }}
                    data-testid={`bulletins-breakdown-mode-${k}`}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]/40 ${
                      on ? "text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"
                    }`}
                    style={on ? { background: C.orange } : undefined}>
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </button>
                );
              })}
            </div>
            {bk && (
              <div className="flex items-center gap-3 text-[11px] font-semibold text-muted-ink" data-testid="bulletins-breakdown-summary">
                <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" style={{ color: C.orange }} />{bk.persons_count} pers.</span>
                <span className="inline-flex items-center gap-1"><Store className="h-3.5 w-3.5" style={{ color: C.orange }} />{bk.lieux_count} lieux</span>
                <span className="tabular-nums">= {Number(bk.total || 0).toLocaleString("fr-FR")} bulletins</span>
              </div>
            )}
          </div>

          {/* Liste classée */}
          <div className="pb-2">
            {loading && !bk ? (
              <div className="space-y-2 px-5 py-2">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="h-8 w-8 animate-pulse rounded-full bg-[rgba(24,24,24,0.06)]" />
                    <div className="h-4 flex-1 animate-pulse rounded bg-[rgba(24,24,24,0.06)]" />
                  </div>
                ))}
              </div>
            ) : isEmpty ? (
              <div className="flex flex-col items-center justify-center gap-1 px-5 py-8 text-center">
                <MapPin className="h-6 w-6 text-muted-ink opacity-50" />
                <p className="text-sm font-semibold text-ink">Aucun bulletin sur cette période</p>
                <p className="text-xs text-muted-ink">La répartition s'affichera dès les premières remontées.</p>
              </div>
            ) : (
              <div className={expanded ? "max-h-80 overflow-y-auto" : ""}>
                {shown.map((r, i) => (
                  mode === "person" ? (
                    <BreakdownRow key={r.rd_id || r.name || i} rank={i + 1}
                      color={r.color} initials={r.initials} title={r.name}
                      metaIcon={MapPin}
                      meta={[r.top_lieu, r.lieux_count > 1 ? `${r.lieux_count} lieux` : null].filter(Boolean).join(" · ")}
                      count={r.count} pct={(r.count || 0) / (max || 1)} assoc={r.assoc}
                      delay={i * 0.03} />
                  ) : (
                    <BreakdownRow key={r.lieu || i} rank={i + 1}
                      color={r.color} icon={Store} title={r.lieu}
                      metaIcon={Users}
                      meta={`${r.persons_count} ${r.persons_count > 1 ? "personnes" : "personne"}`}
                      count={r.count} pct={(r.count || 0) / (max || 1)} assoc={r.assoc}
                      delay={i * 0.03} />
                  )
                ))}
              </div>
            )}
          </div>

          {/* Voir tout / Réduire */}
          {rows.length > 6 && (
            <div className="flex justify-center border-t border-[var(--border-soft)] px-5 py-2">
              <button onClick={() => setExpanded((v) => !v)} data-testid="bulletins-breakdown-toggle-all"
                className="inline-flex items-center gap-1 text-xs font-bold text-[var(--shariti-orange)] transition-colors hover:underline">
                {expanded ? "Réduire" : `Voir les ${rows.length} ${mode === "person" ? "collaborateurs" : "lieux"}`}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
              </button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}


export function BulletinsLiveWidget({ onOpenPointage }) {
  const [data, setData] = useState(null);
  const [active, setActive] = useState("today");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [refreshing, setRefreshing] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  // Personnalisé
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [custom, setCustom] = useState(null);
  const [customLoading, setCustomLoading] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  // Navigation jour par jour (uniquement en vue « Aujourd'hui ») : 0 = aujourd'hui,
  // 1 = hier, 2 = avant-hier, … On peut ainsi remonter le fil des jours passés.
  const [dayOffset, setDayOffset] = useState(0);
  const [dayData, setDayData] = useState(null);
  const [dayLoading, setDayLoading] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/dashboard/bulletins-live");
      setData(r.data);
      setLastUpdated(Date.now());
    } catch (_) { /* silencieux : on garde l'ancienne valeur */ }
    finally { setRefreshing(false); setFirstLoad(false); }
  }, []);

  // Chargement initial + rafraîchissement automatique toutes les 30 s (quasi temps réel).
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  // Ticker « MAJ il y a Xs ».
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Valeurs par défaut du sélecteur personnalisé (mois en cours).
  useEffect(() => {
    if (data?.today_iso && !to) {
      setTo(data.today_iso);
      setFrom(data.today_iso.slice(0, 8) + "01");
    }
  }, [data, to]);

  const runCustom = async () => {
    if (!from || !to) return;
    setCustomLoading(true);
    try {
      const r = await api.get("/dashboard/bulletins-range", { params: { from, to } });
      setCustom(r.data);
    } catch (_) { setCustom(null); }
    finally { setCustomLoading(false); }
  };

  const secsAgo = lastUpdated ? Math.max(0, Math.round((now - lastUpdated) / 1000)) : null;

  // — Navigation jour par jour (vue « Aujourd'hui ») ——————————————————————————
  const selectedDay = data?.today_iso ? shiftIso(data.today_iso, -dayOffset) : null;
  const isDayNav = active === "today" && dayOffset > 0;

  // Récupère le total + comparaison d'un jour passé précis (réutilise bulletins-range).
  useEffect(() => {
    if (!isDayNav || !selectedDay) { setDayData(null); return; }
    let alive = true;
    setDayLoading(true);
    api.get("/dashboard/bulletins-range", { params: { from: selectedDay, to: selectedDay } })
      .then((r) => { if (alive) setDayData(r.data); })
      .catch(() => { if (alive) setDayData(null); })
      .finally(() => { if (alive) setDayLoading(false); });
    return () => { alive = false; };
  }, [isDayNav, selectedDay, lastUpdated]);

  // Toute sortie de la vue « Aujourd'hui » remet le curseur sur le jour courant.
  useEffect(() => { if (active !== "today") setDayOffset(0); }, [active]);

  // Bloc actif (période standard, personnalisée, ou JOUR passé sélectionné).
  const block = isDayNav ? dayData : (active === "custom" ? custom : data?.periods?.[active]);
  const bigValue = useLiveCountUp(block?.current || 0);
  const reached = (block?.progress || 0) >= 100;

  // Confettis quand un objectif est atteint — une seule fois par période (par session
  // de la page), pour rester festif sans être répétitif à chaque rafraîchissement.
  const celebrated = useRef(new Set());
  useEffect(() => {
    if (!block || (block.progress || 0) < 100) return;
    const key = active === "custom"
      ? (custom ? `custom:${custom.range?.start}:${custom.range?.end}` : null)
      : active;
    if (key && !celebrated.current.has(key)) {
      celebrated.current.add(key);
      fireConfetti();
    }
  }, [block, active, custom]);

  // Série pour le sparkline selon la période.
  const spark = useMemo(() => {
    if (!data) return { points: [], caption: "" };
    if (active === "today" || active === "week") {
      return { points: data.series?.daily || [], caption: "30 derniers jours" };
    }
    if (active === "month" || active === "year") {
      return { points: (data.series?.monthly || []).map((m) => ({ count: m.count })), caption: "12 derniers mois" };
    }
    if (active === "custom" && custom?.series) {
      return { points: custom.series.points || [], caption: custom.series.type === "monthly" ? "par mois" : "par jour" };
    }
    return { points: [], caption: "" };
  }, [data, active, custom]);

  const dayLabel = dayOffset === 0 ? "Aujourd'hui"
    : dayOffset === 1 ? "Hier"
    : dayOffset === 2 ? "Avant-hier"
    : (selectedDay ? frDayLabel(selectedDay) : "");
  const rangeLabel = isDayNav
    ? (selectedDay ? frDayLabel(selectedDay) : "")
    : (block?.range ? `Du ${frDate(block.range.start)} au ${frDate(block.range.end)}` : "");
  const prevLabel = isDayNav ? "veille"
    : active === "custom" ? (custom?.prev_label || "période précédente")
    : (block?.prev_label || "");
  // Plage active (période standard / personnalisée / jour passé) → alimente la répartition.
  const activeRange = block?.range;

  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]"
      data-testid="bulletins-live-widget"
    >
      {/* Accent dégradé discret (hero) — < 20% de la surface */}
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full"
        style={{ background: "radial-gradient(circle, rgba(238,114,46,0.14), transparent 70%)" }} />

      {/* En-tête */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border-soft)] px-5 py-4">
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-9 w-9 items-center justify-center rounded-xl"
            style={{ background: "rgba(238,114,46,0.12)" }}>
            <Activity className="h-4.5 w-4.5" style={{ color: C.orange }} />
          </span>
          <div>
            <h3 className="flex items-center gap-2 text-sm font-bold text-ink">
              Bulletins
              <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(31,157,85,0.12)] px-2 py-0.5 text-[10px] font-bold text-[#157A43]">
                <motion.span className="h-1.5 w-1.5 rounded-full" style={{ background: C.green }}
                  animate={{ opacity: [1, 0.25, 1], scale: [1, 0.8, 1] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} />
                TEMPS RÉEL
              </span>
            </h3>
            <p className="text-[11px] text-muted-ink" data-testid="bulletins-updated">
              {secsAgo == null ? "Chargement…" : secsAgo < 2 ? "À l'instant" : `Mis à jour il y a ${secsAgo} s`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={refreshing} data-testid="bulletins-refresh"
            className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-1.5 text-xs font-semibold text-ink transition-colors hover:bg-[var(--surface-2)] disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> Actualiser
          </button>
          <button onClick={onOpenPointage} data-testid="bulletins-open-pointage"
            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-bold text-white transition-transform hover:-translate-y-0.5"
            style={{ background: C.orange }}>
            Pointage <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Sélecteur de période (segmented) */}
      <div className="flex flex-wrap gap-1.5 px-5 pt-4">
        {PERIODS.map((p) => {
          const on = active === p.key;
          return (
            <button key={p.key} onClick={() => setActive(p.key)} data-testid={`bulletins-period-${p.key}`}
              className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]/40 ${
                on ? "text-white shadow-[var(--shadow-sm)]" : "border border-[var(--border-soft)] bg-white text-muted-ink hover:bg-[var(--surface-2)]"
              }`}
              style={on ? { background: C.orange } : undefined}>
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Navigation jour par jour (vue « Aujourd'hui ») : remonter le fil des jours passés */}
      {active === "today" && (
        <div className="flex flex-wrap items-center justify-center gap-2 px-5 pt-3" data-testid="bulletins-day-nav">
          <button onClick={() => setDayOffset((v) => Math.min(v + 1, 365))} data-testid="bulletins-day-prev"
            aria-label="Jour précédent"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[var(--border-soft)] bg-white text-ink transition-colors hover:bg-[var(--surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]/40">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="inline-flex min-w-[10rem] items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold first-letter:uppercase"
            style={{
              background: dayOffset === 0 ? "rgba(31,157,85,0.12)" : "rgba(238,114,46,0.10)",
              color: dayOffset === 0 ? "#157A43" : C.orange,
            }}
            data-testid="bulletins-day-label">
            {dayLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarRange className="h-3.5 w-3.5" />}
            {dayLabel}
          </span>
          <button onClick={() => setDayOffset((v) => Math.max(v - 1, 0))} disabled={dayOffset === 0} data-testid="bulletins-day-next"
            aria-label="Jour suivant"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[var(--border-soft)] bg-white text-ink transition-colors hover:bg-[var(--surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]/40 disabled:cursor-not-allowed disabled:opacity-40">
            <ChevronRight className="h-4 w-4" />
          </button>
          {dayOffset > 0 && (
            <button onClick={() => setDayOffset(0)} data-testid="bulletins-day-today"
              className="ml-1 text-xs font-bold text-[var(--shariti-orange)] transition-colors hover:underline">
              Revenir à aujourd'hui
            </button>
          )}
        </div>
      )}

      {/* Sélecteur de dates (personnalisé) */}
      <AnimatePresence initial={false}>
        {active === "custom" && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden px-5">
            <div className="mt-3 flex flex-wrap items-end gap-3 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3">
              <div className="flex flex-col gap-1">
                <label className="text-[10px] font-bold uppercase tracking-wide text-muted-ink">Du</label>
                <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} data-testid="bulletins-custom-from"
                  className="rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-1.5 text-sm text-ink outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]/40" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] font-bold uppercase tracking-wide text-muted-ink">Au</label>
                <input type="date" value={to} min={from || undefined} max={data?.today_iso || undefined} onChange={(e) => setTo(e.target.value)} data-testid="bulletins-custom-to"
                  className="rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-1.5 text-sm text-ink outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]/40" />
              </div>
              <button onClick={runCustom} disabled={customLoading || !from || !to} data-testid="bulletins-custom-apply"
                className="inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-50"
                style={{ background: C.orange }}>
                {customLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarRange className="h-4 w-4" />}
                Appliquer
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Corps : grand compteur + jauge + sparkline */}
      <div className="grid gap-5 p-5 md:grid-cols-[1.3fr_auto] md:items-center">
        <div className="min-w-0">
          {firstLoad && !data ? (
            <div className="h-24 w-40 animate-pulse rounded-xl bg-[rgba(24,24,24,0.06)]" />
          ) : active === "custom" && !custom ? (
            <div className="flex h-24 items-center text-sm text-muted-ink">
              Choisissez une période puis « Appliquer » pour voir le total.
            </div>
          ) : (isDayNav && !dayData) ? (
            <div className="h-24 w-40 animate-pulse rounded-xl bg-[rgba(24,24,24,0.06)]" />
          ) : (
            <>
              <div className="flex items-end gap-2">
                <AnimatePresence mode="popLayout">
                  <motion.span key={active}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    className="text-6xl font-extrabold leading-none tracking-tight text-ink"
                    data-testid="bulletins-big-value">
                    {bigValue.toLocaleString("fr-FR")}
                  </motion.span>
                </AnimatePresence>
                <span className="mb-1 text-lg font-bold text-muted-ink">bulletins</span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <DeltaBadge delta={block?.delta_pct} prevLabel={prevLabel} />
                {reached && <ObjectiveReachedBadge />}
                <span className="text-xs text-muted-ink" data-testid="bulletins-range-label">{rangeLabel}</span>
              </div>
              <div className="mt-4">
                <Sparkline points={spark.points} caption={spark.caption} />
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-center md:border-l md:border-[var(--border-soft)] md:pl-5">
          {block ? (
            <RadialGauge progress={block.progress || 0} objective={block.objective || 0} auto={!!block.objective_auto} />
          ) : (
            <div className="h-40 w-40 animate-pulse rounded-full bg-[rgba(24,24,24,0.06)]" />
          )}
        </div>
      </div>

      {/* Répartition : qui a fait ces bulletins & où (repliable, sans quitter le cockpit) */}
      <button
        type="button"
        onClick={() => setShowBreakdown((v) => !v)}
        data-testid="bulletins-breakdown-trigger"
        aria-expanded={showBreakdown}
        disabled={!activeRange}
        className="flex w-full items-center justify-between gap-2 border-t border-[var(--border-soft)] px-5 py-3 text-left transition-colors hover:bg-[var(--surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]/30 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className="flex items-center gap-2 text-sm font-bold text-ink">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: "rgba(238,114,46,0.12)" }}>
            <Users className="h-4 w-4" style={{ color: C.orange }} />
          </span>
          Qui a fait ces bulletins&nbsp;?
          <span className="hidden text-xs font-medium text-muted-ink sm:inline">· détail par personne &amp; par lieu</span>
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-ink transition-transform ${showBreakdown ? "rotate-180" : ""}`} />
      </button>

      <BreakdownPanel open={showBreakdown} range={activeRange} refreshKey={lastUpdated} />
    </section>
  );
}

export default BulletinsLiveWidget;
